import React, { useState, useEffect, useMemo } from 'react';
import '../../../assets/scss/components/ui/tables/OperatorDataTable.scss';

const OperatorDataTable = ({ 
  data,
  selectedOperators,
  onSelectOperator, 
  theme,
  headerVisible
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  
  // Expandir automáticamente elementos duplicados al cargar
  useEffect(() => {
    const initialExpandedState = {};
    data.forEach(group => {
      if (group.isDuplicate) {
        initialExpandedState[group.operator_id_number] = false;
      }
    });
    setExpandedGroups(prev => ({...prev, ...initialExpandedState}));
  }, [data]);
  
  const toggleGroup = (operatorId, e) => {
    e.stopPropagation(); // Evitar propagación del evento
    setExpandedGroups(prev => ({
      ...prev,
      [operatorId]: !prev[operatorId]
    }));
  };

  // Aplicar filtros
  const filteredData = useMemo(() => {
    return data.filter(group => {
      // Filtro de búsqueda por ID o nombre
      const operatorId = String(group.operator_id_number || '');
      const operatorName = group.records[0]?.operator_first_names || '';
      const operatorLastName = group.records[0]?.operator_last_names || '';
      const fullName = `${operatorName} ${operatorLastName}`.toLowerCase();
      
      const matchesSearch = 
        operatorId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fullName.includes(searchTerm.toLowerCase());
      
      // Filtro de duplicados
      const matchesDuplicateFilter = showOnlyDuplicates ? group.isDuplicate : true;
      
      return matchesSearch && matchesDuplicateFilter;
    });
  }, [data, searchTerm, showOnlyDuplicates]);
  
  // Calcular estadísticas filtradas
  const filteredStats = useMemo(() => {
    return {
      TOTAL: filteredData.length,
      DUPLICADOS: filteredData.filter(group => group.isDuplicate).length
    };
  }, [filteredData]);
  
  const isSelected = (operatorId, record) => {
    return selectedOperators.some(item => 
      item.operator_id_number === operatorId && item.source_sheet === record.source_sheet
    );
  };
  
  const toggleDuplicatesFilter = () => {
    setShowOnlyDuplicates(prev => !prev);
  };
  
  // Función para obtener valor seguro 
  const getSafeValue = (record, field, defaultValue = 'N/A') => {
    if (!record || typeof record !== 'object') {
      return defaultValue;
    }
    
    if (record[field] !== undefined && record[field] !== null) {
      const value = record[field];
      if (typeof value === 'string' && value.trim() === '') {
        return defaultValue;
      }
      return value;
    }
    
    return defaultValue;
  };
  
  // Función para obtener nombre completo del operador
  const getOperatorFullName = (record) => {
    const firstName = getSafeValue(record, 'operator_first_names', '');
    const lastName = getSafeValue(record, 'operator_last_names', '');
    
    if (!firstName && !lastName) return 'N/A';
    return `${firstName} ${lastName}`.trim();
  };
  
  // Función para renderizar valores booleanos
  const renderBooleanValue = (value) => {
    if (value === true || value === 'true' || value === 'SÍ' || value === 'SI' || value === 'YES' || value === 'X') {
      return 'SÍ';
    }
    if (value === false || value === 'false' || value === 'NO' || value === '.') {
      return 'NO';
    }
    return value || 'N/A';
  };
  
  return (
    <div className={`operator-table-container-${theme} ${!headerVisible ? 'expanded' : ''}`}>
      <div className={`advanced-filters-${theme} ${headerVisible ? 'visible' : 'hidden'}`}>
        {/* Filtros y búsqueda */}
        <div className={`search-filters-${theme}`}>
          <input
            type="text"
            className={`search-input-${theme}`}
            placeholder="Buscar por ID o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <div className={`filter-controls-${theme}`}>
            <label className={`filter-checkbox-${theme}`}>
              <input 
                type="checkbox" 
                checked={showOnlyDuplicates} 
                onChange={toggleDuplicatesFilter}
              />
              <span>Solo duplicados</span>
            </label>
          </div>
        </div>

        {/* Estadísticas */}
        <div className={`operator-stats-${theme}`}>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats.TOTAL}</span>
            <span className="stat-label">Total Operadores</span>
          </div>
          <div className={`stat-item-${theme} stat-duplicates`}>
            <span className="stat-value">{filteredStats.DUPLICADOS}</span>
            <span className="stat-label">Duplicados</span>
          </div>
        </div>
      </div>
      
      <div className={`table-wrapper-${theme} ${!headerVisible ? 'expanded' : ''}`}>
        <table className={`operator-table-${theme}`}>
          <thead>
            <tr>
              <th>Identificación</th>
              <th>Nombre</th>
              <th>Licencia</th>
              <th>Categoría</th>
              <th>Vehículo</th>
              <th>Hoja Excel</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map(group => (
              <React.Fragment key={group.operator_id_number}>
                <tr 
                  className={`group-header-${theme} ${group.isDuplicate ? 'duplicate' : ''}`}
                >
                  <td>
                    <div className={`id-cell-${theme}`}>
                      <span 
                        className="expand-icon"
                        onClick={(e) => toggleGroup(group.operator_id_number, e)}
                      >
                        {expandedGroups[group.operator_id_number] ? '▼' : '►'}
                      </span>
                      {group.operator_id_number}
                      {group.isDuplicate && (
                        <span className={`duplicate-badge-${theme}`}>
                          Duplicado
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{getOperatorFullName(group.records[0])}</td>
                  <td>{getSafeValue(group.records[0], 'operator_license')}</td>
                  <td>{getSafeValue(group.records[0], 'operator_license_category')}</td>
                  <td>{getSafeValue(group.records[0], 'associated_vehicle_plate')}</td>
                  <td>
                    {group.isDuplicate 
                      ? `${group.records.length} hojas encontradas` 
                      : group.records[0]?.source_sheet
                    }
                  </td>
                  <td>
                    {!group.isDuplicate && (
                      <button 
                        className={`select-button-${theme} ${isSelected(group.operator_id_number, group.records[0]) ? 'selected' : ''}`}
                        onClick={() => onSelectOperator(group.operator_id_number, group.records[0])}
                      >
                        {isSelected(group.operator_id_number, group.records[0]) ? 'Seleccionado' : 'Seleccionar'}
                      </button>
                    )}
                  </td>
                </tr>
                
                {expandedGroups[group.operator_id_number] && group.records.map((record, index) => (
                  <tr 
                    key={`${group.operator_id_number}-${index}`} 
                    className={`group-detail-${theme} ${isSelected(group.operator_id_number, record) ? 'selected' : ''}`}
                  >
                    <td colSpan="7">
                      <div className={`record-details-${theme}`}>
                        {/* Información del Operador */}
                        <div className={`record-section-${theme}`}>
                          <h4>Información Personal</h4>
                          <div className={`details-grid-${theme}`}>
                            <div className="detail-item">
                              <span className="label">Nombres:</span>
                              <span className="value">{getSafeValue(record, 'operator_first_names')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Apellidos:</span>
                              <span className="value">{getSafeValue(record, 'operator_last_names')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Fecha Nacimiento:</span>
                              <span className="value">{formatDate(record.operator_birth_date)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Edad:</span>
                              <span className="value">{getSafeValue(record, 'operator_age')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Cédula:</span>
                              <span className="value">{getSafeValue(record, 'operator_id_number')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">RH:</span>
                              <span className="value">{getSafeValue(record, 'operator_blood_type')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Dirección:</span>
                              <span className="value">{getSafeValue(record, 'operator_address')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Teléfono:</span>
                              <span className="value">{getSafeValue(record, 'operator_phone')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Vehículo:</span>
                              <span className="value">{getSafeValue(record, 'associated_vehicle_plate')}</span>
                            </div>
                          </div>
                          
                          <h4>Licencia y Afiliación</h4>
                          <div className={`details-grid-${theme}`}>
                            <div className="detail-item">
                              <span className="label">Licencia:</span>
                              <span className="value">{getSafeValue(record, 'operator_license')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Categoría:</span>
                              <span className="value">{getSafeValue(record, 'operator_license_category')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Vence Licencia:</span>
                              <span className="value">{formatDate(record.operator_license_expiry)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Restricciones:</span>
                              <span className="value">{getSafeValue(record, 'operator_license_restriction')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">ARL:</span>
                              <span className="value">{getSafeValue(record, 'operator_arl')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">EPS:</span>
                              <span className="value">{getSafeValue(record, 'operator_eps')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">AFP:</span>
                              <span className="value">{getSafeValue(record, 'operator_afp')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Nivel Riesgo:</span>
                              <span className="value">{getSafeValue(record, 'operator_risk_level')}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Segunda Columna - Certificaciones */}
                        <div className={`record-section-${theme}`}>
                          <h4>Idoneidad del Operador</h4>
                          <div className={`details-grid-${theme}`}>
                            <div className="detail-item">
                              <span className="label">Certificado para Conducir:</span>
                              <span className="value">{renderBooleanValue(record.operator_certified_to_drive)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Manejo Defensivo:</span>
                              <span className="value">{formatDate(record.defensive_driving_expiry)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Primeros Auxilios:</span>
                              <span className="value">{formatDate(record.first_aid_expiry)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Manejo Sustancias Peligrosas:</span>
                              <span className="value">{formatDate(record.dangerous_substances_expiry)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Manejo Extintores:</span>
                              <span className="value">{formatDate(record.operator_extinguisher_course_expiry)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Inducción HSE:</span>
                              <span className="value">{getSafeValue(record, 'operator_hse_induction')}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Curso Alturas:</span>
                              <span className="value">{formatDate(record.heights_course_expiry)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Exámenes Médicos:</span>
                              <span className="value">{formatDate(record.medical_exams_expiry)}</span>
                            </div>
                          </div>
                          
                          <h4>Vacunas y Seguridad</h4>
                          <div className={`details-grid-${theme}`}>
                            <div className="detail-item">
                              <span className="label">Vacuna Tétano:</span>
                              <span className="value">{formatDate(record.operator_tetanus_vaccine)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Vacuna Fiebre Amarilla:</span>
                              <span className="value">{formatDate(record.operator_yellow_fever_vaccine)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Evidencia MSDS:</span>
                              <span className="value">{renderBooleanValue(record.operator_msds_evidence)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className={`record-actions-${theme}`}>
                          <button
                            className={`select-button-${theme} ${isSelected(group.operator_id_number, record) ? 'selected' : ''}`}
                            onClick={() => onSelectOperator(group.operator_id_number, record)}
                          >
                            {isSelected(group.operator_id_number, record) 
                              ? 'Seleccionado' 
                              : 'Seleccionar este registro'
                            }
                          </button>
                          <div className="source-info">
                            <small>Hoja: {record.source_sheet}</small>
                            <small>Archivo: {record.source_file}</small>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            
            {filteredData.length === 0 && (
              <tr className={`no-results-${theme}`}>
                <td colSpan="7">No se encontraron resultados</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Función para formatear fechas
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    // Verificar formato ISO
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-CO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
    }
    
    // Si no es formato ISO, intentar parsear como fecha genérica
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('es-CO');
    }
    
    // Si es un string sin parsear, devolverlo tal cual
    return dateString;
  } catch (e) {
    return String(dateString);
  }
};

export default OperatorDataTable;